
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { useNotify } from '@/hooks/useNotify';
import { useAuth } from '@/contexts/AuthContext';
import { AlertCircle, Info, Loader2 } from 'lucide-react';
import ContractPdfUpload from './ContractPdfUpload';
import TextareaWithDynamicFields from './TextareaWithDynamicFields';
import RichTextEditorWithDynamicFields from './RichTextEditorWithDynamicFields';
import InputWithDynamicFields from './InputWithDynamicFields';
import { useDocumentDefaults } from './useDocumentDefaults';
import SignatureBuilder from '@/components/settings/signature-builder/SignatureBuilder';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import ResponsiveTabsList from '@/components/shared/ResponsiveTabsList';

const documentDefaultsSchema = z.object({
  // Payment Terms
  default_payment_terms: z.string().optional(),
  default_payment_days: z.number().min(0).max(365).optional(),
  
  // Footers
  estimate_footer: z.string().optional(),
  invoice_footer: z.string().optional(),
  credit_note_footer: z.string().optional(),
  
  // Special Instructions
  default_special_instructions: z.string().optional(),
  
  // Contract Terms
  default_contract_terms: z.string().optional(),
  warranty_terms: z.string().optional(),
  other_standard_contract_terms: z.string().optional(),
  standard_contract_pdf_url: z.string().optional(),
  
  // Document Settings
  estimate_validity_days: z.number().min(1).max(365).optional(),
  invoice_due_days: z.number().min(1).max(365).optional(),
  
  // Email Templates
  estimate_email_template: z.string().optional(),
  invoice_email_template: z.string().optional(),
  estimate_email_subject: z.string().optional(),
  invoice_email_subject: z.string().optional(),
  
  // Material and Delivery
  material_delivery_terms: z.string().optional(),
  
  // Change Orders
  change_order_approval_process: z.string().optional(),
  
  // Safety and Compliance
  safety_requirements_text: z.string().optional(),
  insurance_requirements: z.string().optional(),
  permit_requirements_text: z.string().optional(),
  
  // Operations
  cleanup_procedures: z.string().optional(),
  quality_assurance_terms: z.string().optional(),
  
  // Financial Terms
  default_deposit_percentage: z.number().min(0).max(100).optional(),
  final_payment_terms: z.string().optional(),
});

type DocumentDefaultsFormData = z.infer<typeof documentDefaultsSchema>;

const DynamicFieldsInfo = () => (
  <Card className="mb-4 border-blue-200 bg-blue-50">
    <CardContent className="pt-4">
      <div className="flex items-start gap-2">
        <Info className="h-5 w-5 text-blue-600 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-2">Dynamic Fields Available:</p>
          <p className="text-xs">
            Use the "Add Dynamic Field" button above text areas to insert fields like user names, company info, customer details, addresses, and today's date. These will be automatically filled when documents are generated.
          </p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const DocumentDefaultsForm = () => {
  const notify = useNotify();
  const { profile, user } = useAuth();
  const { documentDefaults, loading, isAdmin, isSaving, saveDocumentDefaults } = useDocumentDefaults();
  const [isInitialized, setIsInitialized] = useState(false);
  const [emailSignatureTemplate, setEmailSignatureTemplate] = useState("");
  
  // Fetch company data for signature builder preview
  const { data: company } = useQuery({
    queryKey: ['company-for-signature-builder', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return null;
      const { data, error } = await supabase
        .from('companies')
        .select('name, email, company_phone_number, website, logo_url')
        .eq('id', profile.company_id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.company_id,
  });
  
  const form = useForm<DocumentDefaultsFormData>({
    resolver: zodResolver(documentDefaultsSchema),
    defaultValues: {
      default_payment_terms: "Net 30",
      default_payment_days: 30,
      estimate_validity_days: 30,
      invoice_due_days: 30,
      default_deposit_percentage: 20,
    },
  });

  useEffect(() => {
    if (documentDefaults && !isInitialized) {
      // Only reset form values once when data is first loaded
      form.reset({
        default_payment_terms: documentDefaults.default_payment_terms || "Net 30",
        default_payment_days: documentDefaults.default_payment_days || 30,
        estimate_footer: documentDefaults.estimate_footer || "",
        invoice_footer: documentDefaults.invoice_footer || "",
        credit_note_footer: documentDefaults.credit_note_footer || "",
        default_special_instructions: documentDefaults.default_special_instructions || "",
        default_contract_terms: documentDefaults.default_contract_terms || "",
        warranty_terms: documentDefaults.warranty_terms || "",
        other_standard_contract_terms: documentDefaults.other_standard_contract_terms || "",
        estimate_validity_days: documentDefaults.estimate_validity_days || 30,
        invoice_due_days: documentDefaults.invoice_due_days || 30,
        estimate_email_template: documentDefaults.estimate_email_template || "",
        invoice_email_template: documentDefaults.invoice_email_template || "",
        estimate_email_subject: documentDefaults.estimate_email_subject || "",
        invoice_email_subject: documentDefaults.invoice_email_subject || "",
        material_delivery_terms: documentDefaults.material_delivery_terms || "",
        change_order_approval_process: documentDefaults.change_order_approval_process || "",
        safety_requirements_text: documentDefaults.safety_requirements_text || "",
        insurance_requirements: documentDefaults.insurance_requirements || "",
        permit_requirements_text: documentDefaults.permit_requirements_text || "",
        cleanup_procedures: documentDefaults.cleanup_procedures || "",
        quality_assurance_terms: documentDefaults.quality_assurance_terms || "",
        default_deposit_percentage: documentDefaults.default_deposit_percentage || 20,
        final_payment_terms: documentDefaults.final_payment_terms || "",
        standard_contract_pdf_url: documentDefaults.standard_contract_pdf_url || "",
      });
      setEmailSignatureTemplate(documentDefaults.email_signature_template || "");
      setIsInitialized(true);
    }
  }, [documentDefaults, form, isInitialized]);

  const onSubmit = async (data: DocumentDefaultsFormData) => {
    // Include the signature template in the save
    const dataWithSignature = {
      ...data,
      email_signature_template: emailSignatureTemplate,
    };
    const success = await saveDocumentDefaults(dataWithSignature);
    if (success) {
      notify.success("Document defaults have been saved successfully");
    }
  };

  const handleSaveSignature = async () => {
    const success = await saveDocumentDefaults({
      email_signature_template: emailSignatureTemplate,
    });
    if (success) {
      notify.success("Email signature template has been saved successfully");
    }
  };

  const handlePdfUpload = (url: string | null) => {
    form.setValue('standard_contract_pdf_url', url || '');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <span>Loading document defaults...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!isAdmin && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-orange-800">
              <AlertCircle className="h-5 w-5" />
              <p className="text-sm">
                Only company administrators can edit document defaults. You can view the current settings below.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <DynamicFieldsInfo />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Tabs defaultValue="payment" className="w-full">
            <ResponsiveTabsList className="mb-4">
              <TabsTrigger value="payment">Payment & Financial</TabsTrigger>
              <TabsTrigger value="documents">Documents & Templates</TabsTrigger>
              <TabsTrigger value="contract">Contract & Legal</TabsTrigger>
              <TabsTrigger value="operations">Operations & Safety</TabsTrigger>
              <TabsTrigger value="signature">Email Signature</TabsTrigger>
            </ResponsiveTabsList>

            <TabsContent value="payment" className="space-y-6">
              {/* Payment & Financial Defaults */}
              <Card className={!isAdmin ? "opacity-60" : ""}>
                <CardHeader>
                  <CardTitle>Payment & Financial Defaults</CardTitle>
                  <CardDescription>
                    Set default payment terms and financial settings for all documents
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="default_payment_terms"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Default Payment Terms</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g., Net 30, Due on Receipt" {...field} disabled={!isAdmin} />
                          </FormControl>
                          <FormDescription>
                            Standard payment terms for estimates and invoices
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="default_payment_days"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Payment Due Days</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              placeholder="30" 
                              {...field}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '') field.onChange('');
                                else { const num = parseInt(val); if (!isNaN(num)) field.onChange(num); }
                              }}
                              onBlur={() => {
                                const num = typeof field.value === 'number' ? field.value : parseInt(String(field.value));
                                if (isNaN(num)) field.onChange(0);
                              }}
                              disabled={!isAdmin}
                            />
                          </FormControl>
                          <FormDescription>
                            Number of days for payment due date
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="default_deposit_percentage"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Default Deposit Percentage (%)</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              step="0.01"
                              placeholder="20.00" 
                              {...field}
                              value={field.value ?? ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '') field.onChange('');
                                else { const num = parseFloat(val); if (!isNaN(num)) field.onChange(num); }
                              }}
                              onBlur={() => { if ((field.value as any) === '' || field.value === undefined) field.onChange(0); }}
                              disabled={!isAdmin}
                            />
                          </FormControl>
                          <FormDescription>
                            Standard deposit percentage required upfront
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="final_payment_terms"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Final Payment Terms</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g., Upon completion and approval" {...field} disabled={!isAdmin} />
                          </FormControl>
                          <FormDescription>
                            Terms for final payment release
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="estimate_validity_days"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Estimate Validity (Days)</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              placeholder="30" 
                              {...field}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '') field.onChange('');
                                else { const num = parseInt(val); if (!isNaN(num)) field.onChange(num); }
                              }}
                              onBlur={() => {
                                const num = typeof field.value === 'number' ? field.value : parseInt(String(field.value));
                                if (isNaN(num) || num < 1) field.onChange(30);
                              }}
                              disabled={!isAdmin}
                            />
                          </FormControl>
                          <FormDescription>
                            How long estimates remain valid
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  
                    <FormField
                      control={form.control}
                      name="invoice_due_days"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Invoice Due Days</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              placeholder="30" 
                              {...field}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '') field.onChange('');
                                else { const num = parseInt(val); if (!isNaN(num)) field.onChange(num); }
                              }}
                              onBlur={() => {
                                const num = typeof field.value === 'number' ? field.value : parseInt(String(field.value));
                                if (isNaN(num) || num < 1) field.onChange(30);
                              }}
                              disabled={!isAdmin}
                            />
                          </FormControl>
                          <FormDescription>
                            Default days until invoice is due
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="documents" className="space-y-6">
              {/* Email Templates */}
              <Card className={!isAdmin ? "opacity-60" : ""}>
                <CardHeader>
                  <CardTitle>Email Templates</CardTitle>
                  <CardDescription>
                    Configure default email subject lines and templates for estimates and invoices
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="estimate_email_subject"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estimate Email Subject</FormLabel>
                        <InputWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="e.g., Estimate {{estimate_number}} from {{company_name}}"
                          disabled={!isAdmin}
                        />
                        <FormDescription>
                          Default subject line for estimate emails. Supports dynamic fields.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                
                  <FormField
                    control={form.control}
                    name="estimate_email_template"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estimate Email Body</FormLabel>
                        <RichTextEditorWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="Dear {{customer_name}}, Please find attached your estimate from {{company_name}}. Best regards, {{user_full_name}}"
                          disabled={!isAdmin}
                          minHeight="120px"
                        />
                        <FormDescription>
                          Standard email template when sending estimates. Supports rich text formatting.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="invoice_email_subject"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Invoice Email Subject</FormLabel>
                        <InputWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="e.g., Invoice {{invoice_number}} from {{company_name}}"
                          disabled={!isAdmin}
                        />
                        <FormDescription>
                          Default subject line for invoice emails. Supports dynamic fields.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                
                  <FormField
                    control={form.control}
                    name="invoice_email_template"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Invoice Email Body</FormLabel>
                        <RichTextEditorWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="Dear {{customer_name}}, Please find attached your invoice from {{company_name}}. Best regards, {{user_full_name}}"
                          disabled={!isAdmin}
                          minHeight="120px"
                        />
                        <FormDescription>
                          Standard email template when sending invoices. Supports rich text formatting.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Document Footers */}
              <Card className={!isAdmin ? "opacity-60" : ""}>
                <CardHeader>
                  <CardTitle>Document Footers</CardTitle>
                  <CardDescription>
                    Configure default footer content for estimates and invoices
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="estimate_footer"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estimate Footer</FormLabel>
                        <TextareaWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="For questions, contact {{user_full_name}} at {{user_email}} or {{user_phone}}"
                          disabled={!isAdmin}
                          rows={3}
                        />
                        <FormDescription>
                          Text that appears at the bottom of all estimates
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                
                  <FormField
                    control={form.control}
                    name="invoice_footer"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Invoice Footer</FormLabel>
                        <TextareaWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="Thank you for your business! Contact {{company_name}} at {{company_email}} for any questions."
                          disabled={!isAdmin}
                          rows={3}
                        />
                        <FormDescription>
                          Text that appears at the bottom of all invoices
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                
                  <FormField
                    control={form.control}
                    name="credit_note_footer"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Credit Note Footer</FormLabel>
                        <TextareaWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="This credit note confirms a credit balance on your account. Contact {{company_name}} for any queries."
                          disabled={!isAdmin}
                          rows={3}
                        />
                        <FormDescription>
                          Text that appears at the bottom of all credit notes
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                
                  <FormField
                    control={form.control}
                    name="default_special_instructions"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Default Special Instructions</FormLabel>
                        <TextareaWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="Default special instructions for projects..."
                          disabled={!isAdmin}
                          rows={3}
                        />
                        <FormDescription>
                          Special instructions that appear on estimates and invoices
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="contract" className="space-y-6">
              {/* Contract & Legal Defaults */}
              <Card className={!isAdmin ? "opacity-60" : ""}>
                <CardHeader>
                  <CardTitle>Contract & Legal Defaults</CardTitle>
                  <CardDescription>
                    Set default contract terms and legal information
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="default_contract_terms"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Default Contract Terms</FormLabel>
                        <TextareaWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="Standard contract terms and conditions..."
                          disabled={!isAdmin}
                          rows={4}
                        />
                        <FormDescription>
                          Standard terms and conditions for contracts
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div>
                    <FormLabel>Standard Contract PDF Document</FormLabel>
                    <FormDescription className="mb-3">
                      Upload a PDF document containing your standard contract terms
                    </FormDescription>
                    <ContractPdfUpload
                      onFileUpload={handlePdfUpload}
                      currentFileUrl={form.watch('standard_contract_pdf_url')}
                      companyId={profile?.company_id || ''}
                      disabled={!isAdmin}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="warranty_terms"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Warranty Terms</FormLabel>
                        <TextareaWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="Default warranty terms..."
                          disabled={!isAdmin}
                          rows={3}
                        />
                        <FormDescription>
                          Standard warranty information for work performed
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="other_standard_contract_terms"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Other Standard Contract Terms</FormLabel>
                        <TextareaWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="Additional standard contract terms..."
                          disabled={!isAdmin}
                          rows={3}
                        />
                        <FormDescription>
                          Additional contract terms and conditions
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="operations" className="space-y-6">
              {/* Materials & Delivery */}
              <Card className={!isAdmin ? "opacity-60" : ""}>
                <CardHeader>
                  <CardTitle>Materials & Delivery</CardTitle>
                  <CardDescription>
                    Set default terms for material delivery and handling
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="material_delivery_terms"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Standard Material Delivery Terms</FormLabel>
                        <TextareaWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="Materials will be delivered FOB destination, customer responsible for unloading..."
                          disabled={!isAdmin}
                          rows={3}
                        />
                        <FormDescription>
                          Standard terms for material delivery and responsibility
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Change Orders & Processes */}
              <Card className={!isAdmin ? "opacity-60" : ""}>
                <CardHeader>
                  <CardTitle>Change Orders & Approval Processes</CardTitle>
                  <CardDescription>
                    Define standard processes for project changes
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="change_order_approval_process"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Default Change Order Approval Process</FormLabel>
                        <TextareaWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="All changes must be approved in writing before work begins. Change orders require signature within 48 hours..."
                          disabled={!isAdmin}
                          rows={4}
                        />
                        <FormDescription>
                          Standard process for handling and approving change orders
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Safety & Compliance */}
              <Card className={!isAdmin ? "opacity-60" : ""}>
                <CardHeader>
                  <CardTitle>Safety & Compliance Requirements</CardTitle>
                  <CardDescription>
                    Set standard safety, insurance, and permit requirements
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="safety_requirements_text"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Standard Safety Requirements</FormLabel>
                        <TextareaWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="All personnel must wear appropriate PPE. OSHA standards will be followed at all times..."
                          disabled={!isAdmin}
                          rows={3}
                        />
                        <FormDescription>
                          Standard safety requirements for all projects
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                
                  <FormField
                    control={form.control}
                    name="insurance_requirements"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Default Insurance Requirements</FormLabel>
                        <TextareaWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="Contractor maintains general liability insurance of $1,000,000. Certificate of insurance available upon request..."
                          disabled={!isAdmin}
                          rows={3}
                        />
                        <FormDescription>
                          Standard insurance requirements and coverage details
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                
                  <FormField
                    control={form.control}
                    name="permit_requirements_text"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Standard Permit Requirements</FormLabel>
                        <TextareaWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="All necessary permits will be obtained before work begins. Permit costs are included in estimate unless otherwise noted..."
                          disabled={!isAdmin}
                          rows={3}
                        />
                        <FormDescription>
                          Standard text regarding permit requirements and responsibilities
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Operations & Quality */}
              <Card className={!isAdmin ? "opacity-60" : ""}>
                <CardHeader>
                  <CardTitle>Operations & Quality Standards</CardTitle>
                  <CardDescription>
                    Define standard procedures for cleanup and quality assurance
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="cleanup_procedures"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Default Cleanup Procedures</FormLabel>
                        <TextareaWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="Daily cleanup will be performed. Final cleanup includes removal of all debris and materials..."
                          disabled={!isAdmin}
                          rows={3}
                        />
                        <FormDescription>
                          Standard cleanup procedures and responsibilities
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                
                  <FormField
                    control={form.control}
                    name="quality_assurance_terms"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Standard Quality Assurance Terms</FormLabel>
                        <TextareaWithDynamicFields
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="All work will be performed to industry standards. Quality inspections will be conducted at key milestones..."
                          disabled={!isAdmin}
                          rows={3}
                        />
                        <FormDescription>
                          Standard quality assurance processes and standards
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="signature" className="space-y-6">
              {/* Email Signature Template */}
              <Card className={!isAdmin ? "opacity-60" : ""}>
                <CardHeader>
                  <CardTitle>Company Email Signature Template</CardTitle>
                  <CardDescription>
                    Design a company-wide email signature template using dynamic fields. All team members will use this template with their personal information automatically filled in.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md mb-4">
                    <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Dynamic Fields for Signatures:</p>
                      <p className="text-xs">
                        Use fields like <code className="bg-blue-100 px-1 rounded">{"{{user_first_name}}"}</code>, <code className="bg-blue-100 px-1 rounded">{"{{user_last_name}}"}</code>, <code className="bg-blue-100 px-1 rounded">{"{{user_role}}"}</code>, <code className="bg-blue-100 px-1 rounded">{"{{user_phone}}"}</code>, <code className="bg-blue-100 px-1 rounded">{"{{user_email}}"}</code>, <code className="bg-blue-100 px-1 rounded">{"{{company_name}}"}</code>, <code className="bg-blue-100 px-1 rounded">{"{{company_phone}}"}</code>, <code className="bg-blue-100 px-1 rounded">{"{{company_email}}"}</code>, <code className="bg-blue-100 px-1 rounded">{"{{company_website}}"}</code> to personalize each user's signature.
                      </p>
                    </div>
                  </div>
                  
                  <SignatureBuilder
                    value={emailSignatureTemplate}
                    onChange={setEmailSignatureTemplate}
                    userProfile={{
                      first_name: profile?.first_name || 'John',
                      last_name: profile?.last_name || 'Smith',
                      company_position: profile?.company_position || 'Team Member',
                      personal_phone_number: profile?.personal_phone_number || '(555) 123-4567',
                      email: user?.email || 'john@example.com',
                    }}
                    company={company || {
                      name: 'Your Company',
                      email: 'info@company.com',
                      company_phone_number: '(555) 000-0000',
                      website: 'www.company.com',
                    }}
                    disabled={!isAdmin}
                  />
                </CardContent>
              </Card>

              {isAdmin && (
                <div className="flex justify-end">
                  <Button type="button" size="lg" onClick={handleSaveSignature} disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      "Save Signature Template"
                    )}
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {isAdmin && (
            <div className="flex justify-end">
              <Button type="submit" size="lg" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  "Save Document Defaults"
                )}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
};

export default DocumentDefaultsForm;
